/** Shared field lists, option sets, and built-in fallback palettes for the
 * "Farben" card on both design pages (`Verein › Design` and `Plattform ›
 * Design (Global)`) — extracted here instead of duplicated per page, and
 * reused by `AppShell.tsx` so the actually-rendered app and the two
 * settings pages' live preview never drift apart.
 *
 * The Anmelden-/Abmelden-/Vielleicht-Buttons (`response-button-style.ts`)
 * used to have their own equivalent "Antwort-Buttons" card with the same
 * org-override / platform-default fallback chain, removed again on request
 * (§ Nutzeranfrage) — the look in effect at that point (Größe "Groß"/12px,
 * unverändertes grün/rot/orange-Palette) is now the fixed, non-configurable
 * app default below, there is no more override layer to fall back through.
 *
 * Every setting exists in two independent variants, `Light`/`Dark` — an org
 * (or the platform default) can customize each mode separately; an org/
 * platform that never touched a mode falls through to these built-ins for
 * it. The `Light` values are unchanged from the app's original look (so an
 * org that configured colors before dark mode existed sees no change); the
 * `Dark` values are a from-scratch "modern dark UI" set — saturated,
 * lower-lightness fills with a bright, readable text color per role/field,
 * the same tinted-pill convention already used for status colors in
 * `index.css`'s `.dark` overrides, rather than reusing the light pastel
 * tones on a dark background (illegible, which is the bug this fixes). */

export type ColorKey =
  | 'primary'
  | 'secondary'
  | 'headerBackground'
  | 'headerText'
  | 'sidebarBackground'
  | 'contentBackground';
export type OpacityKey = 'sidebarOpacity' | 'contentOpacity';
export type GradientToKey = 'sidebarBackgroundTo' | 'contentBackgroundTo';
export type AngleKey = 'sidebarGradientAngle' | 'contentGradientAngle';

export const COLOR_FIELDS: Array<[ColorKey, string]> = [
  ['primary', 'Akzentfarbe (Buttons, Links)'],
  ['secondary', 'Sekundärfarbe'],
  ['headerBackground', 'Kopfleiste — Hintergrund'],
  ['headerText', 'Kopfleiste — Text'],
];

/** Background + opacity + optional gradient — a transparent value lets the
 * uploaded background image show through instead of the sidebar/content
 * area always fully covering it. */
export const TRANSLUCENT_FIELDS: Array<[ColorKey, OpacityKey, GradientToKey, AngleKey, string]> = [
  ['sidebarBackground', 'sidebarOpacity', 'sidebarBackgroundTo', 'sidebarGradientAngle', 'Seitenleiste — Hintergrund'],
  ['contentBackground', 'contentOpacity', 'contentBackgroundTo', 'contentGradientAngle', 'Inhaltsbereich — Hintergrund'],
];

export const BUILTIN_HEADER_HEIGHT = 56;
/** Aus (§ Nutzeranfrage) — der Header hatte zuvor fest `border-slate-200`,
 * was gegen einen dunklen `headerBackground`/ein Hintergrundbild als
 * unerwünschte "weiße Umrandung" auffiel. Wer ihn möchte, schaltet ihn
 * gezielt mit eigener Farbe ein (Verein › Design, "Rahmen Header"). */
export const BUILTIN_HEADER_BORDER_ENABLED = false;
export const BUILTIN_HEADER_BORDER_COLOR_LIGHT = '#e2e8f0';
export const BUILTIN_HEADER_BORDER_COLOR_DARK = '#334155';
export const BUILTIN_BOXED_WIDTH = 1152;
/** Today's always-on look — the app renders boxed unless an org or the
 * platform default explicitly turns it off. */
export const BUILTIN_BOXED_LAYOUT_ENABLED = true;
export const BUILTIN_GRADIENT_ANGLE = 135;

/** Light mode — identical to the app's original, pre-dark-mode built-in
 * look, kept pixel-for-pixel so nothing changes for an org that configured
 * this before dark mode existed. */
export const BUILTIN_COLOR_LIGHT: Record<ColorKey, string> = {
  primary: '#2563eb',
  secondary: '#0f172a',
  headerBackground: '#ffffff',
  headerText: '#0f172a',
  sidebarBackground: '#ffffff',
  contentBackground: '#f8fafc',
};
export const BUILTIN_GRADIENT_TO_LIGHT: Record<GradientToKey, string> = {
  sidebarBackgroundTo: '#e2e8f0',
  contentBackgroundTo: '#e0f2fe',
};

/** Dark mode — the same values `index.css`'s old blanket `.dark { ... }`
 * override used to hard-code (so the *unconfigured* look is unchanged),
 * now a real fallback layer instead of a CSS `!important` override — an
 * org/platform admin can move off these per field, same as light mode. */
export const BUILTIN_COLOR_DARK: Record<ColorKey, string> = {
  primary: '#3b82f6',
  secondary: '#e2e8f0',
  headerBackground: '#0f172a',
  headerText: '#e2e8f0',
  sidebarBackground: '#1e293b',
  contentBackground: '#0f172a',
};
export const BUILTIN_GRADIENT_TO_DARK: Record<GradientToKey, string> = {
  sidebarBackgroundTo: '#334155',
  contentBackgroundTo: '#1e1b4b',
};

export type ResponseButtonRole = 'accepted' | 'declined' | 'maybe';

export const BUILTIN_FONT_SIZE = 12;
/** Entspricht der ehemaligen Größe "Groß" — siehe Kommentar oben. */
export const BUILTIN_BUTTON_PADDING = '6px 14px';

/** Light mode — unchanged from the original hardcoded green/red/amber-100
 * look (see the old `DEFAULTS` in response-button-style.ts). */
export const BUILTIN_BUTTON_LIGHT: Record<ResponseButtonRole, { bg: string; text: string }> = {
  accepted: { bg: '#dcfce7', text: '#15803d' },
  declined: { bg: '#fee2e2', text: '#b91c1c' },
  maybe: { bg: '#fef3c7', text: '#b45309' },
};

/** Dark mode — a saturated, low-lightness fill per role with a bright text
 * color on top (green-950/green-400, etc.), the "modern dark UI" pill look
 * instead of the light pastels going straight onto a dark background. */
export const BUILTIN_BUTTON_DARK: Record<ResponseButtonRole, { bg: string; text: string }> = {
  accepted: { bg: '#14532d', text: '#4ade80' },
  declined: { bg: '#450a0a', text: '#f87171' },
  maybe: { bg: '#451a03', text: '#fbbf24' },
};
